import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { MapCoreApi } from "@fortex/map";
import { Button, Modal, errorMessage } from "@fortex/map";
import { PublicMapForm, emptyPublicMapDraft, publicMapLink, updateOfDraft, validatePublicMapDraft } from "../admin/publicmaps/PublicMapForm";
import type { PublicMapDraft } from "../admin/publicmaps/PublicMapForm";
import { adminPaths } from "../app/AppMenu";
import { useApp } from "../app/context";
import type { FieldErrors } from "../admin/shared/CrudPage";

export function draftOfCore(core: MapCoreApi): PublicMapDraft {
  const state = core.store.getState();
  const checked = core.layers.list().filter(a => (a.root === "maps" || a.root === "layers") && a.selected);
  const layers = checked.map(snapshot => {
    const entry = core.layers.entry(snapshot.id);
    const idName = entry?.idName ?? snapshot.key;

    return { idName, defaultOn: true, searchable: entry?.kind === "folder" };
  });

  return {
    ...emptyPublicMapDraft(),
    lat: state.view.center[0].toFixed(6),
    lon: state.view.center[1].toFixed(6),
    zoom: String(Math.round(state.view.zoom)),
    layers
  };
}

export function CreatePublicMapDialog({ core, open, onClose }: { core: MapCoreApi | null; open: boolean; onClose: () => void }) {
  const { api, labels, config, toast } = useApp();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<PublicMapDraft | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);

  useEffect(() => {
    if (open && draft === null && core !== null) setDraft(draftOfCore(core));
  }, [open, draft, core]);

  const close = () => {
    setDraft(null);
    setErrors({});
    setSavedId(null);
    onClose();
  };

  const save = async () => {
    if (draft === null) return;

    const found = validatePublicMapDraft(draft, labels);

    setErrors(found);

    if (Object.keys(found).length > 0) return;

    setSaving(true);

    try {
      const saved = await api.createPublicMap(updateOfDraft(draft));

      setDraft({ ...draft, id: saved.id });
      setSavedId(saved.id);
      toast("success", labels.publicMapSaved);
    } catch (reason) {
      toast("danger", errorMessage(reason, labels));
    } finally {
      setSaving(false);
    }
  };

  const footer =
    savedId === null ? (
      <>
        <Button kind="ghost" size="sm" label={labels.cancel} onClick={close} disabled={saving} />
        <Button kind="primary" size="sm" icon="check" label={labels.save} busy={saving} onClick={() => void save()} data-fxmap="public-map-save" />
      </>
    ) : (
      <>
        <Button kind="ghost" size="sm" icon="link" label={labels.publicMapOpenAdmin} onClick={() => navigate(adminPaths.publicMaps)} />
        <Button kind="primary" size="sm" label={labels.close} onClick={close} />
      </>
    );

  return (
    <Modal open={open && draft !== null} title={labels.publicMapCreate} onClose={close} closeLabel={labels.close} footer={footer} width="min(720px, calc(100% - 32px))" dataFxmap="public-map-dialog">
      {draft !== null ? <PublicMapForm api={api} labels={labels} draft={draft} setDraft={setDraft} errors={errors} readOnly={savedId !== null} link={savedId === null ? null : publicMapLink(config, savedId)} /> : null}
    </Modal>
  );
}
